import { z } from 'zod'

import { formatDateOnly } from '@/lib/format'
import { isTimeOfDay, normalizeTime } from '@/lib/hr'
import { messages } from '@/lib/messages'
import type { components } from '@/types/api'

export type GirisCikis = 'G' | 'Ç'

export interface MovementValue {
  girisCikis: GirisCikis
  date: string
  time: string
  description: string
}

/** RHF `formState.errors` (düz alanlar) → alan bileşenlerinin beklediği mesaj sözlüğü. */
export function fieldMessages<T extends object>(errors: {
  [K in keyof T]?: { message?: string }
}): Partial<Record<keyof T, string>> {
  const out: Partial<Record<keyof T, string>> = {}
  for (const key of Object.keys(errors) as Array<keyof T>) out[key] = errors[key]?.message
  return out
}

/** Yeniden gönderme formları: talep alanları + zorunlu revizyon mesajı. */
export type MovementResubmitValue = MovementValue & { message: string }
export type LeaveResubmitValue = LeaveFormValue & { message: string }

/** API'deki izin hali: tam günde "YYYY-MM-DD", saatlikte "YYYY-MM-DD HH:MM" (İstanbul). */
export type LeaveValues = components['schemas']['LeaveValues']

/** İzin güncelleme formu: sınırlar tarih + saat olarak ayrı tutulur, saat yalnız saatlikte. */
export interface LeaveFormValue {
  tur: string
  saatlik: boolean
  startDate: string
  startTime: string
  endDate: string
  endTime: string
  description: string
}

export function leaveFormValue(values: LeaveValues, description = ''): LeaveFormValue {
  const [startDate = '', startTime = ''] = values.baslangic.split(' ')
  const [endDate = '', endTime = ''] = values.bitis.split(' ')
  return {
    tur: values.tur,
    saatlik: values.saatlik,
    startDate,
    startTime,
    endDate,
    endTime,
    description,
  }
}

export function leaveValues(value: LeaveFormValue): LeaveValues {
  const bound = (date: string, time: string) =>
    value.saatlik ? `${date} ${normalizeTime(time)}` : date
  return {
    tur: value.tur,
    saatlik: value.saatlik,
    baslangic: bound(value.startDate, value.startTime),
    bitis: bound(value.endDate, value.endTime),
  }
}

/** Biçim, sıra ve açıklama; değişiklik ve iş kuralları (milad, tür eşleşmesi) backend'de. */
export function leaveValid(value: LeaveFormValue): boolean {
  if (!value.tur || !value.startDate || !value.endDate || !value.description.trim()) return false
  if (!value.saatlik) return value.startDate <= value.endDate
  const start = normalizeTime(value.startTime)
  const end = normalizeTime(value.endTime)
  if (!isTimeOfDay(start) || !isTimeOfDay(end)) return false
  return `${value.startDate} ${start}` < `${value.endDate} ${end}`
}

export function sameLeave(a: LeaveValues, b: LeaveValues): boolean {
  return (
    a.tur === b.tur && a.saatlik === b.saatlik && a.baslangic === b.baslangic && a.bitis === b.bitis
  )
}

const r = messages.hr.approvals

const DESCRIPTION_MAX = 2000

/** Giriş/çıkış formunun alanları; kurallar `movementRules`ta (yeniden gönderme aynı kuralları paylaşır). */
const movementObject = z.object({
  girisCikis: z.enum(['G', 'Ç']),
  date: z.string().min(1, r.leave.dateRequired),
  time: z.string(),
  description: z.string(),
})

function movementRules(value: MovementValue, ctx: z.RefinementCtx) {
  if (!isTimeOfDay(normalizeTime(value.time)))
    ctx.addIssue({ code: 'custom', path: ['time'], message: r.timeInvalid })
  if (!value.description.trim())
    ctx.addIssue({ code: 'custom', path: ['description'], message: r.descriptionRequired })
}

/** Giriş/çıkış formu (react-hook-form): saat biçimi ve açıklama; zaman sınırları sunucu ön kontrolünde. */
export const movementFormSchema = movementObject.superRefine(movementRules)

/** Zorunlu revizyon mesajı (yeniden gönderme). */
function messageRule(value: { message: string }, ctx: z.RefinementCtx) {
  if (!value.message.trim())
    ctx.addIssue({ code: 'custom', path: ['message'], message: r.messageRequired })
}

/** Giriş/çıkış yeniden gönderme: `movementFormSchema` + revizyon mesajı. */
export const movementResubmitSchema = movementObject
  .extend({ message: z.string() })
  .superRefine((value, ctx) => {
    movementRules(value, ctx)
    messageRule(value, ctx)
  })

const leaveObject = z.object({
  tur: z.string(),
  saatlik: z.boolean(),
  startDate: z.string(),
  startTime: z.string(),
  endDate: z.string(),
  endTime: z.string(),
  description: z.string(),
})

/** İzin kuralları: `leaveValid`'in alan alan hali; her kural ilgili alanın altında görünür. */
function leaveRules(value: LeaveFormValue, ctx: z.RefinementCtx) {
  const issue = (field: keyof LeaveFormValue, message: string) =>
    ctx.addIssue({ code: 'custom', path: [field], message })
  if (!value.tur) issue('tur', r.leave.typeRequired)
  if (!value.startDate) issue('startDate', r.leave.dateRequired)
  if (!value.description.trim()) issue('description', r.descriptionRequired)
  if (!value.saatlik) {
    if (!value.endDate) issue('endDate', r.leave.dateRequired)
    else if (value.startDate && value.endDate < value.startDate)
      issue('endDate', r.leave.endBeforeStart)
    return
  }
  const start = normalizeTime(value.startTime)
  const end = normalizeTime(value.endTime)
  if (!isTimeOfDay(start)) issue('startTime', r.timeInvalid)
  if (!isTimeOfDay(end)) issue('endTime', r.timeInvalid)
  else if (isTimeOfDay(start) && `${value.startDate} ${start}` >= `${value.endDate} ${end}`)
    issue('endTime', r.leave.endTimeBeforeStart)
}

/** İzin formu. */
export const leaveFormSchema = leaveObject.superRefine(leaveRules)

/** İzin yeniden gönderme: `leaveFormSchema` + revizyon mesajı. */
export const leaveResubmitSchema = leaveObject
  .extend({ message: z.string() })
  .superRefine((value, ctx) => {
    leaveRules(value, ctx)
    messageRule(value, ctx)
  })

/** Yalnız açıklama sorulan formlar (izin iptali, hareket silme). */
export type DescriptionValue = { description: string }
export const descriptionFormSchema = z.object({
  description: z
    .string()
    .max(DESCRIPTION_MAX)
    .refine((value) => value.trim().length > 0, r.descriptionRequired),
})

/** İptal/silme talebinin yeniden gönderilmesi: açıklama + zorunlu revizyon mesajı. */
export type CancelResubmitValue = DescriptionValue & { message: string }
export const cancelResubmitSchema = descriptionFormSchema.extend({
  message: z.string().refine((value) => value.trim().length > 0, r.messageRequired),
})

/** Sunucunun izin talebi alan yolları → form alanı (`useRecordEditor` `serverFields`); saatlikte sınır saattedir. */
export function leaveServerFields(saatlik: boolean) {
  return {
    'leave.tur': 'tur',
    'leave.baslangic': saatlik ? 'startTime' : 'startDate',
    'leave.bitis': saatlik ? 'endTime' : 'endDate',
  } as const
}

/** Sunucunun giriş/çıkış talebi alan yolları → form alanı. */
export const MOVEMENT_SERVER_FIELDS = {
  occurred_at: 'time',
  giris_cikis: 'girisCikis',
} as const

/** "YYYY-MM-DD" ya da "YYYY-MM-DD HH:MM" -> "GG.AA.YYYY[ SS:DD]". */
export function formatLeaveBound(value: string): string {
  const [date = '', time] = value.split(' ')
  return time ? `${formatDateOnly(date)} ${time}` : formatDateOnly(date)
}

export function formatLeaveRange(values: LeaveValues): string {
  return values.baslangic === values.bitis
    ? formatLeaveBound(values.baslangic)
    : `${formatLeaveBound(values.baslangic)} – ${formatLeaveBound(values.bitis)}`
}
